import React, { useState, useRef, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./custom.scss";
import "./BookshareReader.css";
import { Container, Spinner } from "react-bootstrap";
import EpubViewer from "./components/EpubViewer";
import ErrorViewer from "./components/ErrorViewer";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AboutPanel from "./components/AboutPanel";
import NavigationPanel from "./components/NavigationPanel";
import SettingsPanel from "./components/SettingsPanel";
import { useAnnotations } from "./contexts/AnnotationsContext";
import { useFeature } from "./contexts/FeatureFlagContext";
import { AnnotationType } from "./utils/annotator/annotationsHelper";
import { DEFAULT_FONT_SIZE, SLIDER_FONT_SIZES } from "./utils/enums";
import ViewerSettings from "./utils/ViewerSettings.js";
import { getTextExcerpt } from "./utils/DomUtils";
import { getTopVoice, getFilteredVoices } from "./utils/SpeechUtils";
import _throttle from "lodash.throttle";
import useHotkeys from "./hooks/useHotkeys.js";
import LocationTracker from "./utils/location/LocationTracker.js";
import LocationStorageProvider from "./utils/location/LocationStorageProvider.js";
import LocationSyncClient from "./utils/location/LocationSyncClient.js";
import { TtsState } from "./utils/TtsState";
import { useTts } from "./contexts/TtsContext";

const COLOR_LABEL_CUTOFF = 830;
const synth = window.speechSynthesis;

function getViewerSettings() {
  const localViewerSettings = JSON.parse(
    localStorage.getItem("viewerSettings"),
  );
  return localViewerSettings
    ? validateViewerSettings(localViewerSettings)
    : new ViewerSettings();
}

function validateViewerSettings(localViewerSettings) {
  const objectsHaveSameProps = (obj1, obj2) => {
    const sameLength = Object.keys(obj1).length === Object.keys(obj2).length;
    const sameProps = Object.keys(obj1).every((key) =>
      obj2.hasOwnProperty(key),
    );
    return sameLength && sameProps;
  };

  if (
    !SLIDER_FONT_SIZES.find((s) => s.value === localViewerSettings.fontSize)
  ) {
    localViewerSettings.fontSize = DEFAULT_FONT_SIZE;
  }

  const defaults = new ViewerSettings();
  // if the keys are the same between local and default, we can proceed as usual
  // if the keys are not the same, we need to copy old local settings over to a new ViewerSettings
  if (objectsHaveSameProps(localViewerSettings, defaults)) {
    return Object.assign(defaults, localViewerSettings);
  } else {
    Object.assign(defaults, localViewerSettings);
    localStorage.setItem("viewerSettings", JSON.stringify(defaults));
    return defaults;
  }
}

function gtmEvent(details) {
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push(details);
  }
}

export default function BookshareReader({ bookshareId, isRnib }) {
  const {
    initializeAnnotations,
    addBookmark,
    isNoteBeingEntered,
    openSelectNoteButton,
    closeSelectNoteButton,
    registerViewAllNotes,
    registerSelectionProvider,
    handleCreateNote,
    closeNoteModal,
    handleCreateHighlight,
    handleEditNote,
    handleDisplayReset,
    setNoteButtonsBlocked,
  } = useAnnotations();

  const [metadata, setMetadata] = useState(null);
  const [spine, setSpine] = useState(null);
  const [pageList, setPageList] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [navigationBaseUrl, setNavigationBaseUrl] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentPrintPage, setCurrentPrintPage] = useState(null);
  const [currentTocItem, setCurrentTocItem] = useState(null);
  const tts = useTts();
  const [viewerSettings, setViewerSettings] = useState(() =>
    getViewerSettings(),
  );
  const [availableVoices, setAvailableVoices] = useState(() =>
    synth.getVoices(),
  );
  const [showResetSettingsModal, setShowResetSettingsModal] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);
  const [textSelection, setTextSelection] = useState(null);
  const [navTab, setNavTab] = useState(null);
  const [annotationFilter, setAnnotationFilter] = useState(undefined);
  const [annotationToScrollTo, setAnnotationToScrollTo] = useState(null);
  const [focusPage, setFocusPage] = useState(false);
  const [showWhereAmI, setShowWhereAmI] = useState(false);
  const [bookHasMath, setBookHasMath] = useState(false);

  const epubViewerRef = useRef(null);
  const firstRenderCompleted = useRef(false);

  const locationTrackerRef = useRef(null);
  if (!locationTrackerRef.current) {
    const tracker = new LocationTracker(parseInt(bookshareId));
    tracker.storageProvider = new LocationStorageProvider();
    tracker.locationSyncClient = isRnib ? null : new LocationSyncClient();
    locationTrackerRef.current = tracker;
  }

  const throttledLocationUpdateRef = useRef(null);
  if (!throttledLocationUpdateRef.current) {
    throttledLocationUpdateRef.current = _throttle(
      (cfi) => locationTrackerRef.current.updateLocation(cfi),
      1000,
      { trailing: true },
    );
  }

  const annotationsEnabled = useFeature("annotations");
  const isMenuOpen = settingsOpen || navOpen || aboutOpen;
  const isModalDisplayed = isMenuOpen || isNoteBeingEntered();

  const hotkeyManager = useHotkeys(
    {
      openTableOfContents: showTableOfContents,
      openListOfBookmarks: showListOfBookmarks,
      addBookmark: handleBookmarkButtonClicked,
      addNote: () => {
        if (isNoteBeingEntered()) {
          closeNoteModal();
        } else {
          handleCreateNote();
        }
      },
      addHighlight: handleCreateHighlight,
      goBackToBookshare: returnToBookshare,
      openWhereAmI: openWhereAmI,
      goToPage: showPageInput,
      openSettingsPane: toggleSettingsModal,
      startTts: () => tts.handlePlayTts(),
      stopTts: () => tts.interrupt(),
      skipAheadTts: () => tts.skipAhead(),
      goBackTts: () => tts.skipBack(),
      scrollLeft: handleScrollLeft,
      scrollRight: handleScrollRight,
      openAboutPanel: toggleAboutModal,
    },
    isModalDisplayed,
  );

  const setVoices = useCallback(async () => {
    const voices = synth.getVoices();
    if (!metadata || voices.length === 0) return;

    const validVoices = getFilteredVoices(
      voices,
      [window.navigator.language, metadata.language],
      "en",
    );
    setAvailableVoices(validVoices);

    if (
      viewerSettings.voice !== "" &&
      validVoices.find((v) => v.voiceURI === viewerSettings.voice)
    ) {
      return;
    }

    const topVoice = await getTopVoice(
      validVoices,
      [window.navigator.language, metadata.language],
      "en",
    );
    if (topVoice) {
      const newSettings = Object.assign(new ViewerSettings(), viewerSettings);
      newSettings.voice = topVoice.voiceURI;
      setViewerSettings(newSettings);
    }
  }, [metadata, viewerSettings, setAvailableVoices, setViewerSettings]);

  useEffect(() => {
    synth.onvoiceschanged = setVoices;
  }, [setVoices]);

  useEffect(() => {
    if (metadata) setVoices();
  }, [metadata, setVoices]);

  useEffect(() => {
    registerViewAllNotes(showNotesList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerSelectionProvider(({ pauseTts = true } = {}) => {
      const didAutoPause = pauseTts && tts.state === TtsState.Playing;
      if (didAutoPause) tts.pause();
      return getSelectionContext(didAutoPause);
    });
  });

  useEffect(() => {
    setNoteButtonsBlocked(isModalDisplayed);
  }, [isModalDisplayed, setNoteButtonsBlocked]);

  function handleRenditionRelocated(newLocation, tocHref, pageTitle) {
    if (process.env.NODE_ENV !== "production") {
      console.log("Location updated:");
      console.log(
        `Start index ${newLocation.start.index}, href ${newLocation.start.href}, ` +
          `cfi ${newLocation.start.cfi}, displayed ${newLocation.start.displayed.page} of ` +
          `${newLocation.start.displayed.total} pages`,
      );
      console.log(
        `End index ${newLocation.end.index}, href ${newLocation.end.href}, ` +
          `cfi ${newLocation.end.cfi}, displayed ${newLocation.end.displayed.page} of ` +
          `${newLocation.end.displayed.total} pages`,
      );
      if (newLocation.atStart || newLocation.atEnd) {
        console.log(
          `Reading position is at ${newLocation.atStart ? "start" : "end"} of book.`,
        );
      }
    }
    setCurrentLocation(newLocation);
    setCurrentTocItem(navigation.get(tocHref));
    setCurrentPrintPage(pageTitle);
    throttledLocationUpdateRef.current(newLocation.start.cfi);
  }

  async function handleMetadataLoaded(meta) {
    setMetadata(meta);
    document.title = meta.title
      ? `Bookshare Reader: ${meta.title}`
      : "Bookshare Reader";
  }

  function handleSpineLoaded(sp) {
    setSpine(sp);
    setBookHasMath(sp.items.some((item) => item.properties.includes("mathml")));
  }

  function handlePageListLoaded(pl) {
    setPageList(pl);
  }

  function handleNavigationLoaded(nav, baseUrl) {
    if (process.env.NODE_ENV !== "production") {
      console.log("Navigation loaded");
      console.log(nav);
    }
    setNavigation(nav);
    setNavigationBaseUrl(baseUrl);
  }

  function handleBookRendered(section, view) {
    if (!firstRenderCompleted.current) {
      firstRenderCompleted.current = true;
      initializeAnnotations();
    }
    gtmEvent({
      event: "pageview",
      page_location: `${new URL(section.url).pathname}`,
      page_title: `${section.document.title}`,
    });
  }

  function toggleNavModal() {
    if (!navOpen) setFocusPage(false);
    setNavOpen((prev) => !prev);
  }

  function handleNavLinkClicked(target) {
    setNavOpen(false);
    tts.stop();
    handleDisplaySequenceStart();
    epubViewerRef.current.rendition.display(target, true);
  }

  function toggleSettingsModal() {
    setSettingsOpen((prev) => !prev);
  }

  function handleSettingsChanged(newSettings) {
    setViewerSettings(newSettings);
    localStorage.setItem("viewerSettings", JSON.stringify(newSettings));
  }

  function getCfi(useSelectedText = false) {
    let textRange,
      cfi = null;
    if (textSelection !== null && useSelectedText) {
      textRange = epubViewerRef.current.rendition.getRange(textSelection);
      cfi = epubViewerRef.current.cfiFromRange(textRange).toString();
    } else if (tts.isActive()) {
      textRange = tts.getLocation();
      cfi = epubViewerRef.current.cfiFromRange(textRange).toString();
    } else {
      cfi = currentLocation.start.cfi;
      textRange = epubViewerRef.current.rendition
        .getRange(currentLocation.start.cfi)
        .cloneRange();
      const endRange = epubViewerRef.current.rendition.getRange(
        currentLocation.end.cfi,
      );
      textRange.setEnd(endRange.endContainer, endRange.endOffset);
    }
    return { textRange, cfi };
  }

  function handleBookmarkButtonClicked() {
    const locator = { index: currentLocation.start.index };
    const { textRange, cfi } = getCfi(false);

    if (cfi !== null) {
      const snippetLength = 64;
      let snippet = getTextExcerpt(textRange) || "Bookmark";
      if (snippet.length > snippetLength) {
        snippet = snippet.substring(0, snippetLength) + "\u2026";
      }
      addBookmark(cfi, snippet, locator);
    }
  }

  function getSelectionContext(resumeOnDismiss) {
    const { textRange, cfi } = getCfi(true);
    const locator = { index: currentLocation.start.index };

    const onDismiss = () => {
      if (resumeOnDismiss) tts.resume();
      setTextSelection(null);
      epubViewerRef.current?.clearSelection();
    };

    return {
      textRange,
      cfi,
      locator,
      onDismiss,
    };
  }

  function handleTextSelectionChanged(selection) {
    setTextSelection(selection);
    if (selection === null) {
      closeSelectNoteButton();
    }
  }

  function handleTextSelectionMouseup(getViewportRect) {
    if (!getViewportRect) return;
    openSelectNoteButton({ getViewportRect });
  }

  function cancelTts() {
    tts.interrupt();
  }

  function handleErrorRaised(err) {
    setError(err);
    setShowLoadingSpinner(false);
  }

  function handleErrorCleared() {
    setError(null);
  }

  function handleResetSettingsClicked() {
    setShowResetSettingsModal((prev) => !prev);
  }

  function handleDisplaySequenceStart() {
    handleDisplayReset();
    setShowLoadingSpinner(true);
    setTextSelection(null);
  }

  function handleDisplaySequenceEnd() {
    setShowLoadingSpinner(false);
  }

  function returnToBookshare() {
    window.location.href = isRnib
      ? process.env.REACT_APP_RNIB_BASE_URL + "account"
      : process.env.REACT_APP_BOOKSHARE_BASE_URL + "myBookshare";
  }

  function showTableOfContents() {
    setNavTab("toc");
    toggleNavModal();
  }

  function showListOfBookmarks() {
    if (annotationsEnabled) {
      setNavTab("annotations");
      setAnnotationFilter(AnnotationType.Bookmark);
    } else {
      setNavTab("bookmarks");
    }
    setAnnotationToScrollTo(null);
    toggleNavModal();
  }

  function showNotesList() {
    setNavTab("annotations");
    setAnnotationFilter(AnnotationType.Note);
    setAnnotationToScrollTo(null);
    toggleNavModal();
  }

  function handleAnnotationClicked(annotation) {
    setNavTab("annotations");
    setAnnotationFilter(annotation.type);
    setAnnotationToScrollTo(annotation);
    setNavOpen(true);
  }

  function showPageInput() {
    setNavTab("toc");
    setFocusPage(true);
    toggleNavModal();
  }

  function closeNav() {
    setNavTab(null);
    setAnnotationToScrollTo(null);
    toggleNavModal();
  }

  function openWhereAmI() {
    setShowWhereAmI((prev) => !prev);
  }

  function closeWhereAmI() {
    setShowWhereAmI(false);
  }

  function handleScrollLeft() {
    !isModalDisplayed && epubViewerRef.current?.handleArrowPrevClick();
  }

  function handleScrollRight() {
    !isModalDisplayed && epubViewerRef.current?.handleArrowNextClick();
  }

  function toggleAboutModal() {
    setAboutOpen((prev) => !prev);
  }

  const voiceLanguage =
    metadata && metadata.language
      ? metadata.language
      : window.navigator.language;

  return (
    <Container fluid className="d-flex flex-column px-0" id="fullscreen">
      <div id="statusRegion" aria-live="polite" role="status">
        {showLoadingSpinner && (
          <div className="loading-spinner">
            <Spinner animation="border" style={{ color: viewerSettings.color }}>
              <span className="sr-only">Loading</span>
            </Spinner>
          </div>
        )}
        {!showLoadingSpinner && <span className="sr-only">Loaded</span>}
      </div>
      <Header
        title={metadata && metadata.title}
        onSettingsButtonClicked={toggleSettingsModal}
        onReturnToBookshareButtonClicked={returnToBookshare}
      />
      <EpubViewer
        ref={epubViewerRef}
        bookshareId={bookshareId}
        metadata={metadata}
        modalOpen={isModalDisplayed}
        voices={availableVoices}
        viewerSettings={viewerSettings}
        onRenditionRelocated={handleRenditionRelocated}
        onMetadataLoaded={handleMetadataLoaded}
        onSpineLoaded={handleSpineLoaded}
        onPageListLoaded={handlePageListLoaded}
        onNavigationLoaded={handleNavigationLoaded}
        onBookRendered={handleBookRendered}
        onErrorRaised={handleErrorRaised}
        onDisplaySequenceStart={handleDisplaySequenceStart}
        onDisplaySequenceEnd={handleDisplaySequenceEnd}
        onScrollLeft={handleScrollLeft}
        onScrollRight={handleScrollRight}
        hotkeyManager={hotkeyManager}
        onTextSelectionChanged={handleTextSelectionChanged}
        onTextSelectionMouseup={handleTextSelectionMouseup}
        bookHasMath={bookHasMath}
        loadLastLocation={() => locationTrackerRef.current.loadLocation()}
        onNoteClick={handleEditNote}
        onAnnotationClicked={handleAnnotationClicked}
      />
      <Footer
        currentLocation={currentLocation}
        currentPrintPage={currentPrintPage}
        currentTocItem={currentTocItem}
        spine={spine}
        navigation={navigation}
        pageList={pageList}
        onBookmarkButtonClicked={handleBookmarkButtonClicked}
        onNotesButtonClicked={handleCreateNote}
        showWhereAmI={showWhereAmI}
        onWhereAmIClicked={openWhereAmI}
        onWhereAmIBlur={closeWhereAmI}
        onNavButtonClicked={toggleNavModal}
      />
      <NavigationPanel
        show={navOpen}
        defaultTab={navTab}
        initialFilter={annotationFilter}
        annotationToScrollTo={annotationToScrollTo}
        focusPage={focusPage}
        navigation={navigation}
        baseUrl={navigationBaseUrl}
        onNavHide={closeNav}
        onNavLinkClicked={handleNavLinkClicked}
        pageList={pageList}
        currentTocItem={currentTocItem}
      />
      <SettingsPanel
        viewerSettings={viewerSettings}
        show={settingsOpen}
        onSettingsHide={toggleSettingsModal}
        language={voiceLanguage}
        voices={availableVoices}
        colorLabelCutoff={COLOR_LABEL_CUTOFF}
        onSettingsChanged={handleSettingsChanged}
        showResetSettingsModal={showResetSettingsModal}
        onResetSettingsClicked={handleResetSettingsClicked}
        onVoiceSampleRequested={cancelTts}
        bookHasMath={bookHasMath}
      />
      <AboutPanel
        show={aboutOpen}
        onAboutHide={toggleAboutModal}
        metadata={metadata}
        spine={spine}
      />
      <ErrorViewer
        show={error != null}
        error={error}
        onErrorHide={handleErrorCleared}
        isRnib={isRnib}
      />
    </Container>
  );
}
